import React, {
   CSSProperties,
   FocusEvent,
   KeyboardEvent,
   forwardRef,
   ReactElement,
   ReactNode,
   Ref,
   useEffect, useRef,
   useState, useCallback
} from 'react';
import { InvalidValue, InvalidValueType } from '@unifocus/js-utils';
import { Input, InputRefType } from '../base-input/Input';
import { useCombinedRefs } from '../../../../hooks';

export type ForwardRefFn<R> = <P = unknown>(p: P & React.RefAttributes<R>) => ReactElement | null

export interface ValidatingInputProps<T> {
   name?: string;
   value?: T | InvalidValueType;
   placeholder?: string;
   readOnly?: boolean;
   disabled?: boolean;
   border?: boolean;
   padding?: boolean;
   type?: string;
   inputMode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';
   autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';
   autoComplete?: string | 'off' | 'on';
   maxLength?: number;
   width?: string | number;
   minWidth?: string | number;
   maxWidth?: string | number;
   allowClear?: boolean;
   prefix?: ReactNode;
   suffix?: ReactNode;
   addonBefore?: ReactNode;
   addonAfter?: ReactNode;
   popupTrigger?: ReactElement;
   autoFocus?: boolean;
   format: (value?: T) => string | undefined;
   parse: (value?: string) => T | InvalidValueType | undefined;
   inputValidator?: (value?: string) => boolean;
   inputKeyFilter?: string | RegExp | ((value: string) => boolean);
   className?: string;
   style?: CSSProperties;
   onChange?: (value?: T | InvalidValueType) => void;
   onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
   onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
   onPressEnter?: React.KeyboardEventHandler<HTMLInputElement>;
   onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
   onDirtyStateChange?: (isDirty: boolean) => void;
}

export const ValidatingInput = forwardRef(function ValidatingInput<T>(props: ValidatingInputProps<T>,
                                                                      ref: Ref<InputRefType>) {
   const [inputValue, setInputValue] = useState<string>();

   const inputRef = useRef<InputRefType>(null);
   const combinedRef = useCombinedRefs(inputRef, ref);

   // *** These are used only for onDirtyStateChange event
   const rawInputTextRef = useRef<string>();
   const isDirtyRef = useRef<boolean>(false);
   // ***

   const {
      value: propsValue,
      format: propsFormat,
      parse: propsParse,
      inputValidator,
      onDirtyStateChange,
      onChange,
      onBlur,
      onPressEnter
   } = props;

   useEffect(() => {
      const value = propsValue;

      if (value !== InvalidValue) {
         if (typeof value === 'string') {
            setInputValue(value);
            rawInputTextRef.current = value;
         }
         else {
            const formattedValue = propsFormat(value);
            setInputValue(formattedValue);
            rawInputTextRef.current = formattedValue;
         }
      }
   }, [propsValue, propsFormat]);

   // *** Used only for onDirtyStateChange event
   useEffect(() => {
      const wasDirty = isDirtyRef.current;
      const isDirty = rawInputTextRef.current !== inputValue;
      if (isDirty !== wasDirty) {
         isDirtyRef.current = isDirty;
         if (onDirtyStateChange) {
            onDirtyStateChange(isDirty);
         }
      }
   }, [inputValue, onDirtyStateChange]);
   // ***

   const inputOnChangeHandler = useCallback((value?: string) => {
      rawInputTextRef.current = value; // Used only for onDirtyStateChange event

      if (!inputValidator || inputValidator(value)) {
         setInputValue(value);
      }
   },[inputValidator]);

   const propsOnChangeHandler = useCallback(() => {
      const nativeValue = propsParse(inputValue);
      const newInputValue = nativeValue === undefined
                            ? ''
                            : nativeValue === InvalidValue ? inputValue : propsFormat(nativeValue);

      if (inputValue !== newInputValue) {
         setInputValue(newInputValue);
      }

      if (onChange) {
         onChange(nativeValue);
      }
   },[onChange, propsFormat, propsParse, inputValue]);

   const onBlurHandler = useCallback((event: FocusEvent<HTMLInputElement>) => {

      const cancelButtonCausedBlur = (event.relatedTarget && (event.relatedTarget as HTMLElement).getAttribute('data-iscancelbutton'));

      if (!cancelButtonCausedBlur) {
         propsOnChangeHandler();
      }

      if (onBlur) {
         onBlur(event);
      }
   },[onBlur, propsOnChangeHandler]);

   const onPressEnterHandler = useCallback((event: KeyboardEvent<HTMLInputElement>) => {

      propsOnChangeHandler();

      if (onPressEnter) {
         onPressEnter(event);
      }
   },[onPressEnter, propsOnChangeHandler]);

   return (
      <Input
         ref={combinedRef}
         name={props.name}
         type={'text'}
         inputMode={props.inputMode}
         autoCapitalize={props.autoCapitalize}
         autoComplete={props.autoComplete}
         placeholder={props.placeholder}
         readOnly={props.readOnly}
         disabled={props.disabled}
         allowClear={props.allowClear}
         maxLength={props.maxLength}
         width={props.width}
         minWidth={props.minWidth}
         maxWidth={props.maxWidth}
         border={props.border}
         padding={props.padding}
         prefix={props.prefix}
         suffix={props.suffix}
         addonBefore={props.addonBefore}
         addonAfter={props.addonAfter}
         autoFocus={props.autoFocus}
         popupTrigger={props.popupTrigger}
         className={props.className}
         style={props.style}
         value={inputValue}
         onChange={inputOnChangeHandler}
         onBlur={onBlurHandler}
         onFocus={props.onFocus}
         onPressEnter={onPressEnterHandler}
         onKeyDown={props.onKeyDown}
         inputKeyFilter={props.inputKeyFilter}
      />
   );
}) as <T>(props: ValidatingInputProps<T> & React.RefAttributes<InputRefType>,
          ref: Ref<InputRefType>) => ReactElement;
